import { ScanToOrderPayment } from "../types/types";
import { getCountryPhoneCode } from "./constants";

export function capitalizeFromSnakeCase(value: string = "") {
  return value
    ?.toLowerCase() // Convert the entire string to lowercase
    .replace(/_/g, " ") // Replace underscores with spaces
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize each word
}

export function capitalizeFirst(input: string = "") {
  if (!input) return "";

  return `${input.charAt(0).toUpperCase()}${input.substring(1)}`;
}

export function capitalize(input: string = "") {
  if (!input) return "";
  const words = input.split(" ");
  const result = [];
  for (const w of words) {
    result.push(`${w.charAt(0).toUpperCase()}${w.substring(1)}`);
  }
  return result.join(" ");
}

export function truncate(input: string = "", limit: number = 15) {
  if (input.length > limit) {
    return input.substring(0, limit) + "...";
  }
  return input;
}

export function last4(input: string = "", limit: number = 4) {
  if (input.length > limit) {
    return input.substring(input.length - limit);
  }
  return input;
}

export function capitalizeWithTrunc(input: string = "", limit: number = 15) {
  const capitalized = capitalize(input);
  if (capitalized.length > limit) {
    return capitalized.substring(0, limit) + "...";
  }
  return capitalized;
}

export function getCurrencySymbol(countryCode: string = "USA") {
  if (!countryCode) {
    return "$"; // always return $ in any cases
  }
  let symbol = "$";
  switch (countryCode) {
    case "IND":
      symbol = "₹";
      break;
    case "UK":
      symbol = "£";
      break;
    case "JPN":
      symbol = "¥";
      break;
  }
  return symbol;
}

/**
 * Products have current as USD, INR. This will return $ and ₹.
 *
 * Use this wherever you are getting product prices.
 * @param countryCode
 * @returns
 */
export function getProductCurrencySymbol(currencyCode?: string) {
  if (!currencyCode) {
    return "$"; // always return $ in any cases
  }
  let symbol = "$";
  switch (currencyCode) {
    case "INR":
      symbol = "₹";
      break;
    case "GBP":
      symbol = "£";
      break;
    case "JPY":
      symbol = "¥";
      break;
  }
  return symbol;
}
/**
 * Returns USD for country code USA
 * @param countryCode
 * @returns
 */
export function getCurrencyCode(countryCode: string = "USA") {
  if (!countryCode) {
    return "USD"; // always return $ in any cases
  }
  let symbol = "USD";
  switch (countryCode) {
    case "IND":
      symbol = "INR";
      break;
    case "UK":
      symbol = "GBP";
      break;
    case "JPN":
      symbol = "JPY";
      break;
  }
  return symbol;
}

export function displayPhoneNumber(
  phone?: string,
  countryCode?: string | null,
) {
  if (phone && countryCode) {
    const countryCodeToTrim = getCountryPhoneCode(countryCode);
    return phone.replace(countryCodeToTrim, "");
  }
  return "";
}

export function getRecaptchaKey() {
  return process.env.REACT_APP_RECAPTCHA_KEY ?? "";
}

export const isRecaptchaEnabled =
  process.env.REACT_APP_RECAPTCHA_ENABLED === "true";

/**
 * Saves a remote image to the device. On phones this opens the native share
 * sheet ("Save Image"), which is the only way iOS lets a web page put a file in
 * the photo library; elsewhere it falls back to a plain download.
 * @returns false when neither worked and the caller should tell the user to
 * long press the image instead.
 */
export async function saveImageToDevice(url: string, filename: string) {
  try {
    const response = await fetch(url);
    const blob = await response.blob();

    const file = new File([blob], filename, { type: blob.type });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file] });
      return true;
    }

    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
    return true;
  } catch (err) {
    // the share sheet throws AbortError when the user dismisses it - nothing
    // went wrong and there is nothing to fall back to.
    return (err as Error)?.name === "AbortError";
  }
}

export function formatAppleTokenToFiserv(payment: ScanToOrderPayment) {
  return (
    `${payment.paymentData.details.token.paymentData.data}&ectype=apple` +
    `&ecsig=${payment.paymentData.details.token.paymentData.signature}` +
    `&eckey=${payment.paymentData.details.token.paymentData.header.ephemeralPublicKey}` +
    `&ectid=${payment.paymentData.details.token.paymentData.header.transactionId}` +
    `&echash=` +
    `&ecpublickeyhash=${payment.paymentData.details.token.paymentData.header.publicKeyHash}`
  );
}
